import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "../services/api";
import UserAccessManager from "./UserAccessManager";
import MCPListManager from "./MCPTool";
import MCPMe from "./MCPMe";
import AgentBuilder from "./AgentHub";

const TAB_STORAGE_KEY = "adminDashboard.tabIndex";

function AdminDashboard() {
  const navigate = useNavigate();

  // 탭 인덱스 (세션 보존)
  const [tabIndex, setTabIndex] = useState(0);

  // 로딩/알림
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // 로그인/권한 관련
  const [userId, setUserId] = useState<string>("");
  const [userTier, setUserTier] = useState<number | null>(null);
  const isAdmin = userTier === 0;

  // axios 인증 헤더 (MCPMDetail.tsx 패턴 맞춤)
  const authHeaders = useMemo(
    () => ({
      "x-user-id": localStorage.getItem("user_id") || "",
      "x-access-token": localStorage.getItem("access_token") || "",
      "x-refresh-token": localStorage.getItem("refresh_token") || "",
    }),
    []
  );

  // 비밀번호 변경 다이얼로그 상태
  const [openPwDialog, setOpenPwDialog] = useState(false);
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);

  // 세션의 탭 복구 함수
  const readStoredTab = () => {
    const raw = sessionStorage.getItem(TAB_STORAGE_KEY);
    if (raw == null) return 0;
    const parsed = parseInt(raw.trim(), 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  };

  useEffect(() => {
    const uid = localStorage.getItem("user_id") || "";
    const accessToken = localStorage.getItem("access_token");
    const refreshToken = localStorage.getItem("refresh_token");

    const tierStr = localStorage.getItem("user_tier");
    const tierParsed =
      tierStr != null && tierStr.trim() !== ""
        ? parseInt(tierStr.trim(), 10)
        : null;

    if (!uid || !accessToken || !refreshToken) {
      navigate("/login");
      return;
    }

    setUserId(uid);
    setUserTier(Number.isFinite(tierParsed) ? tierParsed : null);
    setLoading(false);
  }, [navigate]);

  // 탭 구성 로직 수정 (순서 및 이름 변경 반영)
  const tabs = useMemo(() => {
    // 개별 탭 정의
    const tabUserMgr = { label: "사용자 관리", content: <UserAccessManager setErrorMsg={setErrorMsg} /> };
    const tabMyHome = { label: "My Home", content: <MCPMe /> };
    const tabMCPHub = { label: "MCP Tool", content: <MCPListManager setErrorMsg={setErrorMsg} /> };
    const tabAgentHub = { label: "Agent Hub", content: <AgentBuilder setErrorMsg={setErrorMsg} /> }; // 이름 변경됨

    if (isAdmin) {
      // 관리자 순서: 사용자 관리 -> My Home -> MCP Hub -> Agent Hub
      return [
        tabUserMgr,
        tabMyHome,
        tabMCPHub,
        tabAgentHub,
      ];
    }

    // 일반 사용자 순서: My Home -> MCP Hub -> Agent Hub
    return [
      tabMyHome,
      tabMCPHub,
      tabAgentHub,
    ];
  }, [isAdmin]);

  // isAdmin 결정/탭 구성 이후: 저장된 탭 인덱스 복구(범위 보정)
  useEffect(() => {
    const saved = readStoredTab();
    const safeIndex = Math.min(Math.max(saved, 0), Math.max(tabs.length - 1, 0));
    setTabIndex(safeIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, tabs.length]);

  // 탭 변경 시 세션에 저장
  const handleTabChange = (_: React.SyntheticEvent, value: number) => {
    setTabIndex(value);
    sessionStorage.setItem(TAB_STORAGE_KEY, String(value));
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.removeItem(TAB_STORAGE_KEY);
    navigate("/login");
  };

  // 비밀번호 변경 처리 (axios + API_BASE + authHeaders)
  const handleChangePassword = async () => {
    setPwError(null);
    setErrorMsg(null);
    setSuccessMsg(null);

    if (!oldPw || !newPw) {
      setPwError("현재 비밀번호와 새 비밀번호를 입력하세요.");
      return;
    }
    if (newPw !== newPw2) {
      setPwError("새 비밀번호와 확인 비밀번호가 일치하지 않습니다.");
      return;
    }

    setPwLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE}auth/change-password`,
        {
          old_password: oldPw,
          new_password: newPw,
        },
        { headers: authHeaders }
      );

      if (res.data?.success) {
        // ✅ 성공 시: 알림 → 세션 초기화 → 로그인 페이지 이동
        alert("비밀번호가 성공적으로 변경되었습니다. 다시 로그인해주세요.");

        // 세션/스토리지 비우기
        localStorage.clear();
        sessionStorage.clear();

        // 로그인 화면 이동
        navigate("/login");
      } else {
        setPwError(res.data?.error || res.data?.message || "비밀번호 변경에 실패했습니다.");
      }
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        "서버 오류로 비밀번호 변경에 실패했습니다.";
      setPwError(msg);
    } finally {
      setPwLoading(false);
      setOpenPwDialog(false);
      setOldPw("");
      setNewPw("");
      setNewPw2("");
    }
  };


  if (loading) {
    return (
      <Box height="100vh" display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: "#121212", minHeight: "100vh", color: "#fff" }}>
      {/* 전역 알림 */}
      <Stack spacing={1} sx={{ mb: 2 }}>
        {errorMsg && <Alert severity="error">{errorMsg}</Alert>}
        {successMsg && <Alert severity="success">{successMsg}</Alert>}
      </Stack>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight="bold">
            KR AI Agent Hub 대시보드
          </Typography>
          <Typography variant="body2" color="grey.400">
            반갑습니다{userId ? `, ${userId}님` : ""} {isAdmin ? "(관리자)" : "(Hub 사용자)"}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {/* ← Log Out 왼쪽에 Change Password 버튼 배치 */}
          <Button variant="outlined" color="primary" onClick={() => setOpenPwDialog(true)}>
            Change Password
          </Button>
          <Button variant="outlined" color="error" onClick={handleLogout}>
            Log Out
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        textColor="inherit"
        indicatorColor="primary"
      >
        {tabs.map((t, i) => (
          <Tab key={i} label={t.label} />
        ))}
      </Tabs>

      <Box mt={3}>{tabs[tabIndex]?.content}</Box>

      {/* 비밀번호 변경 다이얼로그 */}
      <Dialog open={openPwDialog} onClose={() => setOpenPwDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>비밀번호 변경</DialogTitle>
        <DialogContent>
          {pwError && (
            <Alert severity="error" sx={{ mt: 1, mb: 2 }}>
              {pwError}
            </Alert>
          )}
          <TextField
            margin="dense"
            label="현재 비밀번호"
            type="password"
            fullWidth
            value={oldPw}
            onChange={(e) => setOldPw(e.target.value)}
          />
          <TextField
            margin="dense"
            label="새 비밀번호"
            type="password"
            fullWidth
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
          />
          <TextField
            margin="dense"
            label="새 비밀번호 확인"
            type="password"
            fullWidth
            value={newPw2}
            onChange={(e) => setNewPw2(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPwDialog(false)} color="inherit">
            취소
          </Button>
          <Button onClick={handleChangePassword} disabled={pwLoading} color="primary" variant="contained">
            {pwLoading ? "변경 중..." : "변경"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminDashboard;